import { orchestrator } from "satcheljs";
import { MessageBarType } from "@fluentui/react";
import toggleMuteScheduleGroup from "../actions/toggleMuteScheduleGroup";
import setMuteScheduleGroupStatus from "../actions/setMuteScheduleGroupStatus";
import setToggleLoading from "../actions/setToggleLoading";
import setGlobalMessages from "sh-application/actions/setGlobalMessages";
import { appViewState } from "sh-application/store";
import { UserTeamSettingsDataService, InstrumentationService, ECSConfigService, ECSConfigKey, StaffHubErrorCodes } from "sh-services";
import { UserTeamSettingsEntity } from "sh-models";
import UserTeamSettingsStore from "../store";
import StringsStore from "sh-strings/store";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { showMuteScheduleGroupDialog } from "sh-application/components/muteScheduleGroupDialog";

/**
 * Displays a user-friendly error message when mute schedule group / unmute schedule group action fails.
 */
function showMuteScheduleGroupError(): void {
    const strings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const errorMessage = strings.get("muteScheduleGroupErrorMessage");
    setGlobalMessages(
        appViewState().globalMessageViewState,
        [errorMessage],
        MessageBarType.error,
        null,
        null,
        false,
        true
    );
}

/**
 * Displays a user-friendly error message when loading mute schedule group preference fails.
 */
function showMuteScheduleGroupLoadError(): void {
    const strings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const errorMessage = strings.get("muteScheduleGroupLoadErrorMessage");
    setGlobalMessages(
        appViewState().globalMessageViewState,
        [errorMessage],
        MessageBarType.error,
        null,
        null,
        false,
        true
    );
}

/**
 * Logs telemetry events for mute schedule group / unmute schedule group operations.
 *
 * @remarks
 * Centralizes event logging to ensure consistent telemetry across all code paths
 * (toggled, success, failure). Automatically includes base properties (scheduleGroupId,
 * isMuted) and optionally includes additional properties (lazyCreated, errorMessage).
 *
 * @param eventType - Type of event: "toggled" (action initiated), "success" (completed), or "failed" (error occurred)
 * @param scheduleGroupId - ID of the schedule group being muted/unmuted
 * @param isMuted - New mute state: true if muting, false if unmuting
 * @param additionalProperties - Optional properties:
 *   - lazyCreated: true if UserTeamSettings was created for first time (vs updating existing)
 *   - errorMessage: Error description for failed events
 */
function logMuteScheduleGroupEvent(
    eventType: "toggled" | "success" | "failed",
    scheduleGroupId: string,
    isMuted: boolean,
    additionalProperties?: { lazyCreated?: boolean; errorMessage?: string }
): void {
    const baseProperties = [
        getGenericEventPropertiesObject(InstrumentationService.properties.ScheduleGroupId, scheduleGroupId),
        getGenericEventPropertiesObject(InstrumentationService.properties.IsMuted, isMuted)
    ];

    // Add optional properties
    if (additionalProperties?.lazyCreated !== undefined) {
        baseProperties.push(
            getGenericEventPropertiesObject(InstrumentationService.properties.LazyCreated, additionalProperties.lazyCreated)
        );
    }
    if (additionalProperties?.errorMessage) {
        baseProperties.push(
            getGenericEventPropertiesObject(InstrumentationService.properties.ErrorMessage, additionalProperties.errorMessage)
        );
    }

    // Log appropriate event
    const eventMap = {
        toggled: InstrumentationService.events.MuteScheduleGroupToggled,
        success: InstrumentationService.events.MuteScheduleGroupSuccess,
        failed: InstrumentationService.events.MuteScheduleGroupFailed
    };

    InstrumentationService.logEvent(eventMap[eventType], baseProperties);
}

/**
 * Updates the mutedScheduleGroupIds array by adding or removing a schedule group.
 *
 * @remarks
 * This helper centralizes the logic for maintaining the mutedScheduleGroupIds array,
 * which tracks which schedule groups a user has explicitly muted. By default, users
 * have all groups unmuted, so this array only contains groups that have been muted.
 *
 * Uses Set for O(1) add/delete operations, then converts back to array for storage.
 * Handles null/undefined arrays gracefully.
 *
 * **Important**: This function does NOT mutate the input array - it returns a new array.
 *
 * @param currentMutedIds - Current array of muted schedule group IDs (may be null/undefined)
 * @param scheduleGroupId - ID of the schedule group to add or remove
 * @param isMuted - New mute state:
 *   - true: User is now muting → ADD scheduleGroupId to muted list
 *   - false: User is now unmuting → REMOVE scheduleGroupId from muted list
 *
 * @returns New array with the schedule group added or removed (does not mutate input)
 */
function updateMutedScheduleGroupIds(
    currentMutedIds: string[],
    scheduleGroupId: string,
    isMuted: boolean
): string[] {
    // Convert array to Set for efficient add/delete
    const mutedIds = new Set(currentMutedIds || []);

    if (isMuted) {
        // Now muting → ADD to muted list
        mutedIds.add(scheduleGroupId);
    } else {
        // Now unmuting → REMOVE from muted list
        mutedIds.delete(scheduleGroupId);
    }

    // Convert Set back to array
    return Array.from(mutedIds);
}

/**
 * Performs the actual mute/unmute toggle action after user confirms in the dialog.
 * Contains all the logic for fetching, updating, and saving the mute status.
 */
async function performMuteToggleAction(teamId: string, scheduleGroupId: string): Promise<void> {
    // Set loading state to disable menu item and prevent race conditions
    setToggleLoading(teamId, scheduleGroupId, true);

    try {
        // Variable to hold UserTeamSettings (null if doesn't exist yet)
        let userTeamSettings: UserTeamSettingsEntity | null = null;
        let loadSucceeded = false; // Track whether GET succeeded

        try {
            // Fetch current UserTeamSettings (Memory → IndexedDB → Network)
            // Returns null if doesn't exist yet (lazy creation on first mute)
            userTeamSettings = await UserTeamSettingsDataService.getUserTeamSettings(teamId);

            // Mark that GET succeeded - any errors after this point are save failures
            loadSucceeded = true;
        } catch (error) {
            // Check if error is UserSettingsNotFound (expected for first-time users)
            const isUserSettingsNotFound = (error as any)?.staffHubInnerErrorCode === StaffHubErrorCodes.UserSettingsNotFound;

            if (isUserSettingsNotFound) {
                // UserSettingsNotFound is EXPECTED for first-time users (not an error)
                // Treat as null and continue to lazy creation flow below
                userTeamSettings = null;
                loadSucceeded = true; // This is a successful scenario, not a load failure
            } else {
                // Actual load failure (network error, permissions, etc.)
                logMuteScheduleGroupEvent("failed", scheduleGroupId, true, {
                    errorMessage: error?.message || "Load failed"
                });
                showMuteScheduleGroupLoadError();
                return; // Exit early - can't proceed without data
            }
        }

        // Continue with toggle logic (userTeamSettings is now either valid entity or null)

        // Determine current and new mute state
        const muteScheduleGroupMap = UserTeamSettingsStore().muteScheduleGroupStatusByTeamAndGroup.get(teamId);
        const currentlyMuted = muteScheduleGroupMap ? muteScheduleGroupMap.get(scheduleGroupId) === true : false;
        const newMuteState = !currentlyMuted;

        // Log toggle action initiated
        logMuteScheduleGroupEvent("toggled", scheduleGroupId, newMuteState);

        // Handle lazy creation (settings don't exist yet)
        if (!userTeamSettings) {
            if (!newMuteState) {
                // User clicked to "unmute" but default is already unmuted
                // No-op - don't create settings just to store empty array
                return;
            } else {
                // User is muting first group - CREATE UserSettings
                const defaultSettings = new UserTeamSettingsEntity(
                    null, // id auto-generated
                    null, // eTag null for new entities
                    teamId,
                    [scheduleGroupId] // Start with this one group muted
                );

                // Save new entity (creates in DB, returns with id and eTag)
                await UserTeamSettingsDataService.saveUserTeamSettings(teamId, defaultSettings);

                // Update UI to reflect new state
                setMuteScheduleGroupStatus(teamId, scheduleGroupId, newMuteState);

                // Log success with lazy creation context
                logMuteScheduleGroupEvent("success", scheduleGroupId, newMuteState, { lazyCreated: true });

                return;
            }
        }

        // Optimistic UI update (settings exist, updating existing entity)
        // Update UI IMMEDIATELY before API call for instant feedback
        setMuteScheduleGroupStatus(teamId, scheduleGroupId, newMuteState);

        try {
            // Clone entity to preserve eTag for concurrency control
            const updatedSettings: UserTeamSettingsEntity = UserTeamSettingsEntity.clone(userTeamSettings)!;

            // Update the mutedScheduleGroupIds array using helper
            updatedSettings.mutedScheduleGroupIds = updateMutedScheduleGroupIds(
                updatedSettings.mutedScheduleGroupIds,
                scheduleGroupId,
                newMuteState
            );

            // Save updated entity (Memory → IndexedDB → Network)
            await UserTeamSettingsDataService.saveUserTeamSettings(teamId, updatedSettings);

            // Success! UI already shows correct state from optimistic update
            logMuteScheduleGroupEvent("success", scheduleGroupId, newMuteState, { lazyCreated: false });
        } catch (error) {
            // Handle save failure - REVERT optimistic UI update
            setMuteScheduleGroupStatus(teamId, scheduleGroupId, currentlyMuted);

            // Clear both memory and IndexedDB caches on 409 conflict to prevent stale eTag
            // This ensures manual retry fetches fresh data from network (not stale IndexedDB cache)
            const is409Conflict = (error as any)?.statusCode === 409 || (error as any)?.response?.status === 409;
            if (is409Conflict) {
                const store = UserTeamSettingsStore();
                store.userTeamSettingsByTeam.delete(teamId); // Clear memory
                await UserTeamSettingsDataService.clearCache(teamId); // Clear IndexedDB
            }

            // NOTE: Intentionally NOT implementing automatic retry on 409 eTag conflicts.
            // Rationale: UserTeamSettings is user-scoped, so 409s should be very rare.
            // Implementing refetch-and-retry adds significant complexity for minimal benefit.
            // Cache clear above ensures manual retry works (refetches fresh eTag from network).
            // Monitor MuteScheduleGroupFailed telemetry - if 409 errors become frequent, revisit this decision.

            // Log failure with error details
            logMuteScheduleGroupEvent("failed", scheduleGroupId, newMuteState, {
                errorMessage: error?.message || "Save failed"
            });

            // Show user-friendly error message
            showMuteScheduleGroupError();
        }
    } catch (error) {
        // Handle lazy creation save failure (POST operation)
        // This catch block handles errors thrown by the lazy creation save above
        // Note: GET failures are handled in the earlier catch block above

        // Clear memory cache on 409 conflict so manual retry will work
        const is409Conflict = (error as any)?.statusCode === 409 || (error as any)?.response?.status === 409;
        if (is409Conflict) {
            const store = UserTeamSettingsStore();
            store.userTeamSettingsByTeam.delete(teamId);
        }

        logMuteScheduleGroupEvent("failed", scheduleGroupId, false, {
            errorMessage: error?.message || "Save failed"
        });

        showMuteScheduleGroupError();
    } finally {
        // GUARANTEED cleanup: Clear loading state in all code paths (success, error, early return)
        // This ensures the menu item is re-enabled even if unexpected errors occur
        setToggleLoading(teamId, scheduleGroupId, false);
    }
}

/**
 * Orchestrator for mute/unmute schedule group action.
 * Shows confirmation dialog BEFORE taking action, then proceeds only if user confirms.
 */
export default orchestrator(toggleMuteScheduleGroup, async actionMessage => {
    const { teamId, scheduleGroupId } = actionMessage;

    // Feature flag check - only proceed if mute schedule groups feature is enabled
    if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableMuteScheduleGroups)) {
        return;
    }

    // Check if a toggle is already in progress for this specific schedule group
    const loadingMap = UserTeamSettingsStore().toggleLoadingByTeamAndGroup.get(teamId);
    if (loadingMap?.get(scheduleGroupId)) {
        // Ignore rapid successive clicks - operation already in progress
        return;
    }

    // Determine current and new mute state
    const muteScheduleGroupMap = UserTeamSettingsStore().muteScheduleGroupStatusByTeamAndGroup.get(teamId);
    const currentlyMuted = muteScheduleGroupMap ? muteScheduleGroupMap.get(scheduleGroupId) === true : false;
    const newMuteState = !currentlyMuted;

    // Log dialog shown event
    InstrumentationService.logEvent(
        InstrumentationService.events.MuteScheduleGroupDialogShown,
        [
            getGenericEventPropertiesObject(InstrumentationService.properties.ScheduleGroupId, scheduleGroupId),
            getGenericEventPropertiesObject(InstrumentationService.properties.ActionTaken, newMuteState ? "mute" : "unmute")
        ]
    );

    // Show confirmation dialog BEFORE taking action
    showMuteScheduleGroupDialog(
        newMuteState ? "mute" : "unmute",
        () => {
            // onConfirm callback - proceed with the toggle action
            performMuteToggleAction(teamId, scheduleGroupId);
        },
        () => {
            // onCancel callback - do nothing, user cancelled
        }
    );
});
